import { ZfStack } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { AccountType } from '../../../../api/generated/client';
import { useWealthContext } from '../../../../contexts/wealth-context';
import { EmptyChartMessage } from '../EmptyChartMessage';
import { AssetGroupChartTab } from './AssetGroupChartTab';
import { AssetsLiabilitiesChart } from './AssetLiabilitiesChart';

const DEFAULT_CATEGORY_ID = 0;

interface IProps {
    accountType: AccountType;
}

export const AssetGroupChart: FunctionComponent<IProps> = ({ accountType }) => {
    const { t, i18n } = useTranslation();
    const { activeCategoryId, clientCurrency, setActiveCategoryId, setActiveAccountType, wealthChartDataMap } = useWealthContext();

    useLayoutEffect(() => {
        setActiveCategoryId(DEFAULT_CATEGORY_ID);
        setActiveAccountType(accountType);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const chartData = wealthChartDataMap[accountType];

    if (!chartData?.totalAmount) return <EmptyChartMessage />;

    return (
        <ZfStack direction="column" alignment="center">
            <AssetsLiabilitiesChart accountType={accountType} width={1200} legendWith={350} floatLegend={true} legendPosition={'right'} showNetAmount={false} />
            <ZfStack direction="row" spacing="extra-extra-small">
                <AssetGroupChartTab
                    id={DEFAULT_CATEGORY_ID}
                    currency={clientCurrency}
                    amount={chartData.totalAmount}
                    subTitle={t(chartData.titleTranslationKey)}
                    isActive={activeCategoryId === DEFAULT_CATEGORY_ID}
                    onTabSelect={() => setActiveCategoryId(DEFAULT_CATEGORY_ID)}
                />
                {chartData.visibleCategories.map((category) => (
                    <AssetGroupChartTab
                        key={category.id}
                        id={category.id}
                        currency={clientCurrency}
                        amount={category.totalAmount}
                        subTitle={category.name[i18n.language]}
                        isActive={activeCategoryId === category.id}
                        onTabSelect={() => setActiveCategoryId(category.id)}
                    />
                ))}
            </ZfStack>
        </ZfStack>
    );
};
